import { Toaster } from "react-hot-toast";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import "./App.css";
import {
  AccountDeletionPage,
  AdminAuthCallback,
  AdminAuthScreen,
  AdminDashboard,
  AdminProtectedRoute,
  AuthSelectionScreen,
  ConsentHistoryPage,
  DataTransferAgreementScreen,
  HomeScreen,
  IdentityVerificationCallbackScreen,
  IdentityVerificationScreen,
  InstitutionConnectionManagementPage,
  MarketingConsentPage,
  MhwMaintenanceGuard,
  MyDataManagementPage,
  MyDataRequestAgreementScreen,
  MyDataTermsListPage,
  OperatorAccessLogsPage,
  OperatorAuthCallback,
  OperatorAuthScreen,
  OperatorDashboard,
  OperatorProtectedRoute,
  OperatorUserChatsPage,
  OperatorUsersPage,
  OperatorUserTransmissionDemandsPage,
  PhoneAuthScreen,
  ProtectedRoute,
  PublicRoute,
  ReviewPage,
  SettingsScreen,
  SignupVerificationCallback,
  TermsAgreementScreen,
  TransmissionDemandDetailPage,
  TransmissionDemandEditPage,
  TransmissionDemandNewPage,
  UsageHistoryPage,
} from "./components";
import { SentryErrorBoundary } from "./components/common/SentryErrorBoundary";
import { useAgreementCheck } from "./hooks/useAgreementCheck";
import { useAuth } from "./hooks/useAuth";

// Router 내부에서 실행될 컴포넌트
function AppRoutes() {
  // 새로고침 시 동의 상태 확인 및 리다이렉트
  useAgreementCheck();

  return (
    <Routes>
      {/* 홈 스크린 - 로그인 상태 무관하게 접근 가능 */}
      <Route path="/" element={<HomeScreen />} />
      <Route path="/chat/:chatId" element={<HomeScreen />} />

      {/* 설정 페이지 - 로그인 필요 */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/account-deletion"
        element={
          <MhwMaintenanceGuard>
            <ProtectedRoute>
              <AccountDeletionPage />
            </ProtectedRoute>
          </MhwMaintenanceGuard>
        }
      />
      <Route
        path="/settings/mydata-management"
        element={
          <MhwMaintenanceGuard>
            <ProtectedRoute>
              <MyDataManagementPage />
            </ProtectedRoute>
          </MhwMaintenanceGuard>
        }
      />
      <Route
        path="/settings/mydata-terms-list"
        element={
          <MhwMaintenanceGuard>
            <ProtectedRoute>
              <MyDataTermsListPage />
            </ProtectedRoute>
          </MhwMaintenanceGuard>
        }
      />
      <Route
        path="/settings/consent-history"
        element={
          <MhwMaintenanceGuard>
            <ProtectedRoute>
              <ConsentHistoryPage />
            </ProtectedRoute>
          </MhwMaintenanceGuard>
        }
      />
      <Route
        path="/settings/usage-history"
        element={
          <ProtectedRoute>
            <UsageHistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/marketing-consent"
        element={
          <ProtectedRoute>
            <MarketingConsentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/institution-connection"
        element={
          <MhwMaintenanceGuard>
            <ProtectedRoute>
              <InstitutionConnectionManagementPage />
            </ProtectedRoute>
          </MhwMaintenanceGuard>
        }
      />
      <Route
        path="/settings/institution-connection/new"
        element={
          <MhwMaintenanceGuard>
            <ProtectedRoute>
              <TransmissionDemandNewPage />
            </ProtectedRoute>
          </MhwMaintenanceGuard>
        }
      />
      <Route
        path="/settings/transmission-demand/:transmissionDemandId"
        element={
          <MhwMaintenanceGuard>
            <ProtectedRoute>
              <TransmissionDemandDetailPage />
            </ProtectedRoute>
          </MhwMaintenanceGuard>
        }
      />
      <Route
        path="/settings/transmission-demand/:transmissionDemandId/edit"
        element={
          <MhwMaintenanceGuard>
            <ProtectedRoute>
              <TransmissionDemandEditPage />
            </ProtectedRoute>
          </MhwMaintenanceGuard>
        }
      />

      <Route
        path="/auth/data-transfer"
        element={
          <MhwMaintenanceGuard>
            <DataTransferAgreementScreen />
          </MhwMaintenanceGuard>
        }
      />

      <Route
        path="/auth/mydata-request-agreement"
        element={
          <MhwMaintenanceGuard>
            <ProtectedRoute>
              <MyDataRequestAgreementScreen />
            </ProtectedRoute>
          </MhwMaintenanceGuard>
        }
      />

      {/* 로그인하지 않은 사용자만 접근 가능한 페이지 */}
      <Route
        path="/auth"
        element={
          <MhwMaintenanceGuard>
            <PublicRoute>
              <AuthSelectionScreen />
            </PublicRoute>
          </MhwMaintenanceGuard>
        }
      />
      <Route
        path="/auth/terms"
        element={
          <MhwMaintenanceGuard>
            <PublicRoute>
              <TermsAgreementScreen />
            </PublicRoute>
          </MhwMaintenanceGuard>
        }
      />
      {/* 다날 본인인증 (신규 - 메인 인증 방식) */}
      <Route
        path="/auth/identity-verification"
        element={
          <MhwMaintenanceGuard>
            <PublicRoute>
              <IdentityVerificationScreen />
            </PublicRoute>
          </MhwMaintenanceGuard>
        }
      />
      {/* 모바일 본인인증 콜백 (인증 상태 무관) */}
      <Route
        path="/auth/identity-verification/callback"
        element={
          <MhwMaintenanceGuard>
            <IdentityVerificationCallbackScreen />
          </MhwMaintenanceGuard>
        }
      />
      {/* 기존 SMS 인증 (deprecated - 호환성 유지) */}
      <Route
        path="/auth/phone"
        element={
          <MhwMaintenanceGuard>
            <PublicRoute>
              <PhoneAuthScreen />
            </PublicRoute>
          </MhwMaintenanceGuard>
        }
      />

      {/* 회원가입 콜백 (인증 상태 무관) */}
      <Route
        path="/myhealthway/cert/callback/cert"
        element={
          <MhwMaintenanceGuard>
            <SignupVerificationCallback />
          </MhwMaintenanceGuard>
        }
      />

      {/* 관리자 라우트 */}
      <Route path="/admin/auth" element={<AdminAuthScreen />} />
      <Route path="/admin/auth/callback" element={<AdminAuthCallback />} />
      <Route
        path="/admin/dashboard"
        element={
          <AdminProtectedRoute>
            <AdminDashboard />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/review/:messageId"
        element={
          <AdminProtectedRoute>
            <ReviewPage />
          </AdminProtectedRoute>
        }
      />
      {/* 기본 관리자 경로 -> 대시보드로 리다이렉트 */}
      <Route
        path="/admin"
        element={
          <AdminProtectedRoute>
            <AdminDashboard />
          </AdminProtectedRoute>
        }
      />

      {/* 운영자 라우트 - 서버 사이드 OAuth */}
      <Route path="/operator" element={<OperatorAuthScreen />} />
      <Route path="/operator/auth" element={<OperatorAuthScreen />} />
      <Route
        path="/operator/auth/callback"
        element={<OperatorAuthCallback />}
      />
      <Route
        path="/operator/dashboard"
        element={
          <OperatorProtectedRoute>
            <OperatorDashboard />
          </OperatorProtectedRoute>
        }
      />
      <Route
        path="/operator/users"
        element={
          <OperatorProtectedRoute>
            <OperatorUsersPage />
          </OperatorProtectedRoute>
        }
      />
      <Route
        path="/operator/users/:userId/chats"
        element={
          <OperatorProtectedRoute>
            <OperatorUserChatsPage />
          </OperatorProtectedRoute>
        }
      />
      <Route
        path="/operator/users/:userId/mydata"
        element={
          <OperatorProtectedRoute>
            <OperatorUserTransmissionDemandsPage />
          </OperatorProtectedRoute>
        }
      />
      <Route
        path="/operator/access-logs"
        element={
          <OperatorProtectedRoute>
            <OperatorAccessLogsPage />
          </OperatorProtectedRoute>
        }
      />
      {/* 기본 운영자 경로 -> 대시보드로 리다이렉트 */}
      <Route
        path="/operator"
        element={
          <OperatorProtectedRoute>
            <OperatorDashboard />
          </OperatorProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  // 앱 시작 시 인증 상태 초기화
  useAuth();

  return (
    <SentryErrorBoundary>
      <div className="h-screen-dvh bg-gray-100 md:bg-gray-300 overflow-hidden">
        {/* 데스크톱에서 중앙 고정을 위한 컨테이너 */}
        <div className="max-w-md mx-auto bg-white h-screen-dvh shadow-xl relative overflow-hidden">
          <Router>
            <AppRoutes />
          </Router>
          {/* React Hot Toast */}
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 4000,
              style: {
                background: "#fff",
                color: "#363636",
                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                borderRadius: "8px",
              },
              success: {
                style: {
                  border: "1px solid #22c55e",
                },
              },
              error: {
                style: {
                  border: "1px solid #ef4444",
                },
              },
            }}
          />
        </div>
      </div>
    </SentryErrorBoundary>
  );
}

export default App;
